import { useState, useEffect } from 'react'
import {
  AreaChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { getGrowthAnalytics, getVerticalAnalytics, getDemoVideoAnalytics, getPmfAnalytics } from '../lib/api'
import type { GrowthAnalytics, VerticalAnalytics, VerticalCount, DemoVideoStat, PmfAnalytics } from '../lib/api'
import { useThemeColors } from '../hooks/useThemeColors'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtMinutes(m: number): string {
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const rem = m % 60
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`
}

function fmtMrr(cents: number): string {
  return '$' + (cents / 100).toLocaleString()
}

function fmtPct(v: number | null, sign = true): string {
  if (v === null) return '—'
  return (sign && v > 0 ? '+' : '') + v.toFixed(1) + '%'
}

function WoWBadge({ value }: { value: number | null }) {
  if (value === null) return <span className="text-steel text-body-sm">—</span>
  const positive = value >= 0
  return (
    <span className={positive ? 'text-emerald-400 text-body-sm font-medium' : 'text-red-400 text-body-sm font-medium'}>
      {positive ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
    </span>
  )
}

// ── Linear regression trendline ───────────────────────────────────────────────

function calcTrendline(data: number[]): number[] {
  const n = data.length
  if (n < 2) return data
  const xs = data.map((_, i) => i)
  const ys = data
  const meanX = xs.reduce((a, b) => a + b, 0) / n
  const meanY = ys.reduce((a, b) => a + b, 0) / n
  const denom = xs.reduce((acc, x) => acc + (x - meanX) ** 2, 0)
  if (denom === 0) return data
  const slope = xs.reduce((acc, x, i) => acc + (x - meanX) * (ys[i] - meanY), 0) / denom
  const intercept = meanY - slope * meanX
  return xs.map(x => slope * x + intercept)
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="bg-obsidian border border-carbon rounded p-6 animate-pulse">
      <div className="h-3 bg-carbon rounded w-24 mb-3" />
      <div className="h-7 bg-carbon rounded w-16" />
    </div>
  )
}

function SkeletonChart() {
  return (
    <div className="bg-obsidian border border-carbon rounded p-6 animate-pulse">
      <div className="h-4 bg-carbon rounded w-32 mb-6" />
      <div className="h-[300px] bg-carbon/40 rounded" />
    </div>
  )
}

// ── Custom tooltip ────────────────────────────────────────────────────────────

function CustomTooltip({
  active,
  payload,
  label,
  formatter,
}: {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
  formatter?: (val: number, name: string) => string
}) {
  const c = useThemeColors()
  if (!active || !payload?.length) return null
  return (
    <div
      className="rounded px-3 py-2 text-footnote shadow-lg"
      style={{ background: c.bgSecondary, border: `1px solid ${c.border}` }}
    >
      <div className="text-steel mb-1">{label}</div>
      {payload.map((p, i) => {
        if (p.name === 'trendline') return null
        return (
          <div key={i} style={{ color: p.color }}>
            {formatter ? formatter(p.value, p.name) : p.value}
          </div>
        )
      })}
    </div>
  )
}

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string | number
  sub?: React.ReactNode
}) {
  return (
    <div className="bg-obsidian border border-carbon rounded p-6">
      <div className="text-steel text-footnote uppercase tracking-wide mb-1">{label}</div>
      <div className="text-chalk text-h4">{value}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  )
}

// ── Tab: North Star ───────────────────────────────────────────────────────────

function NorthStarTab({ data }: { data: GrowthAnalytics }) {
  const c = useThemeColors()
  const [showTrend, setShowTrend] = useState(false)
  const { weekly, cumulative, currentWeek, previousWeek, wowGrowth } = data.northStar

  const allZero = weekly.every(w => w.minutes === 0)

  const weeklyWithTrend = (() => {
    const tl = calcTrendline(weekly.map(w => w.minutes))
    return weekly.map((w, i) => ({ ...w, trendline: Math.max(0, tl[i]) }))
  })()

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 tablet:grid-cols-3 gap-4">
        <MetricCard label="This Week" value={fmtMinutes(currentWeek)} />
        <MetricCard label="Prev Week" value={fmtMinutes(previousWeek)} />
        <MetricCard label="WoW Growth" value={fmtPct(wowGrowth, true)} sub={<WoWBadge value={wowGrowth} />} />
      </div>

      {/* Weekly minutes chart */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-chalk font-medium">Weekly Processing Minutes</h3>
          <button
            onClick={() => setShowTrend(!showTrend)}
            className={`text-footnote px-3 py-1 rounded border transition-colors ${
              showTrend
                ? 'border-steel/60 text-chalk bg-carbon/50'
                : 'border-carbon text-steel hover:text-chalk'
            }`}
          >
            Trendline
          </button>
        </div>

        {allZero ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-steel italic text-body-sm text-center">
              No data yet — chart will populate once customers start processing videos
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={weeklyWithTrend}>
              <defs>
                <linearGradient id="nsm-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={c.signal} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={c.signal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
              <XAxis dataKey="week" stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} />
              <YAxis stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip
                content={<CustomTooltip formatter={(v) => fmtMinutes(Math.round(v))} />}
              />
              <Area
                type="monotone"
                dataKey="minutes"
                stroke={c.signal}
                fill="url(#nsm-grad)"
                strokeWidth={2}
                dot={false}
              />
              {showTrend && (
                <Line
                  type="monotone"
                  dataKey="trendline"
                  stroke={c.textMuted}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                  dot={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Cumulative chart */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-4">Cumulative Minutes Processed</h3>
        {allZero ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-steel italic text-body-sm text-center">
              No data yet — chart will populate once customers start processing videos
            </p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={cumulative}>
              <defs>
                <linearGradient id="cumulative-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={c.aurora} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={c.aurora} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
              <XAxis dataKey="week" stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} />
              <YAxis stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip formatter={(v) => fmtMinutes(Math.round(v))} />} />
              <Area
                type="monotone"
                dataKey="minutes"
                stroke={c.aurora}
                fill="url(#cumulative-grad)"
                strokeWidth={2}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

// ── Tab: Acquisition ──────────────────────────────────────────────────────────

function AcquisitionTab({ data }: { data: GrowthAnalytics }) {
  const c = useThemeColors()
  const [showTrend, setShowTrend] = useState(false)
  const {
    weeklySignups,
    cumulativeUsers,
    totalUsers,
    activatedUsers,
    activationRate,
    thisWeekSignups,
    waitlistFunnel,
    w1Retention,
  } = data.acquisition

  const weeklyWithTrend = (() => {
    const tl = calcTrendline(weeklySignups.map(w => w.signups))
    return weeklySignups.map((w, i) => ({ ...w, trendline: Math.max(0, tl[i]) }))
  })()

  const funnelMax = Math.max(waitlistFunnel.total, 1)

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-4">
        <MetricCard label="Total Users" value={totalUsers.toLocaleString()} />
        <MetricCard label="This Week Signups" value={thisWeekSignups.toLocaleString()} />
        <MetricCard
          label="Activation Rate"
          value={fmtPct(activationRate, false)}
          sub={<span className="text-steel text-footnote">{activatedUsers} activated</span>}
        />
        <MetricCard
          label="W1 Retention"
          value={w1Retention !== null ? fmtPct(w1Retention, false) : '—'}
          sub={<span className="text-steel text-footnote">processed video within 7d</span>}
        />
      </div>

      {/* Weekly signups bar chart */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-chalk font-medium">Weekly Signups</h3>
          <button
            onClick={() => setShowTrend(!showTrend)}
            className={`text-footnote px-3 py-1 rounded border transition-colors ${
              showTrend
                ? 'border-steel/60 text-chalk bg-carbon/50'
                : 'border-carbon text-steel hover:text-chalk'
            }`}
          >
            Trendline
          </button>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={weeklyWithTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="week" stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} />
            <YAxis stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="signups" fill={c.signal} radius={[3, 3, 0, 0]} />
            {showTrend && (
              <Line
                type="monotone"
                dataKey="trendline"
                stroke={c.textMuted}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Cumulative users area chart */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-4">Cumulative Users</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={cumulativeUsers}>
            <defs>
              <linearGradient id="cum-users-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={c.aurora} stopOpacity={0.3} />
                <stop offset="95%" stopColor={c.aurora} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="week" stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} />
            <YAxis stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="total"
              stroke={c.aurora}
              fill="url(#cum-users-grad)"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Waitlist funnel */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-5">Waitlist Funnel</h3>
        <div className="space-y-4">
          {[
            { label: 'Waitlisted', count: waitlistFunnel.total, pct: 100 },
            {
              label: 'Invited',
              count: waitlistFunnel.invited,
              pct: Math.round((waitlistFunnel.invited / funnelMax) * 100),
              sub: `${waitlistFunnel.inviteConversionRate}% invite rate`,
            },
            {
              label: 'Signed Up',
              count: waitlistFunnel.signedUp,
              pct: Math.round((waitlistFunnel.signedUp / funnelMax) * 100),
              sub: `${waitlistFunnel.signupConversionRate}% of invited`,
            },
          ].map(({ label, count, pct, sub }) => (
            <div key={label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-steel text-body-sm">{label}</span>
                <span className="text-chalk text-body-sm font-medium">
                  {count.toLocaleString()}
                  {sub && <span className="text-steel text-footnote ml-2">({sub})</span>}
                </span>
              </div>
              <div className="h-2 bg-carbon rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: c.signal,
                    opacity: label === 'Waitlisted' ? 1 : label === 'Invited' ? 0.75 : 0.5,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Tab: Revenue ──────────────────────────────────────────────────────────────

function RevenueTab({ data }: { data: GrowthAnalytics }) {
  const c = useThemeColors()
  const { currentMrr, currentArr, weeklyMrr, wowMrrGrowth, payingUsers, totalUsers, freeToProRate } =
    data.revenue

  return (
    <div className="space-y-6">
      {/* Metric cards */}
      <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-4 gap-4">
        <MetricCard label="MRR" value={fmtMrr(currentMrr)} />
        <MetricCard label="ARR" value={fmtMrr(currentArr)} />
        <MetricCard
          label="WoW MRR Growth"
          value={fmtPct(wowMrrGrowth, true)}
          sub={<WoWBadge value={wowMrrGrowth} />}
        />
        <MetricCard
          label="Free → Pro"
          value={fmtPct(freeToProRate, false)}
          sub={<span className="text-steel text-footnote">{payingUsers} of {totalUsers} users</span>}
        />
      </div>

      {/* MRR over time */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-4">MRR Over Time</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={weeklyMrr}>
            <defs>
              <linearGradient id="mrr-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={c.signal} stopOpacity={0.3} />
                <stop offset="95%" stopColor={c.signal} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
            <XAxis dataKey="week" stroke={c.border} tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} />
            <YAxis
              stroke={c.border}
              tick={{ fill: c.textMuted, fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v: number) => fmtMrr(v)}
            />
            <Tooltip content={<CustomTooltip formatter={(v) => fmtMrr(v)} />} />
            <Area
              type="monotone"
              dataKey="mrr"
              stroke={c.signal}
              fill="url(#mrr-grad)"
              strokeWidth={2}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── Tab: Verticals ────────────────────────────────────────────────────────────

function VerticalBarChart({ title, data }: { title: string; data: VerticalCount[]; dataKey?: 'vertical' | 'replacing' }) {
  const c = useThemeColors()
  if (!data || data.length === 0) {
    return (
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk text-body-sm font-semibold mb-4">{title}</h3>
        <p className="text-steel text-footnote">No data yet</p>
      </div>
    )
  }
  return (
    <div className="bg-obsidian border border-carbon rounded p-6">
      <h3 className="text-chalk text-body-sm font-semibold mb-6">{title}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} layout="vertical" margin={{ left: 0, right: 16, top: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.border} horizontal={false} />
          <XAxis type="number" tick={{ fill: c.textMuted, fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: c.textMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={160}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null
              return (
                <div style={{ background: c.bgSecondary, border: `1px solid ${c.border}`, borderRadius: 3, padding: '8px 12px' }}>
                  <p style={{ color: c.textPrimary, fontSize: 13, fontWeight: 600 }}>{payload[0].payload.label}</p>
                  <p style={{ color: c.signal, fontSize: 12 }}>{payload[0].value} {Number(payload[0].value) === 1 ? 'entry' : 'entries'}</p>
                </div>
              )
            }}
          />
          <Bar dataKey="count" fill={c.signal} radius={[0, 2, 2, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function VerticalsTab({ data }: { data: VerticalAnalytics }) {
  const total = data.total_with_vertical + data.total_without_vertical
  const pct = total > 0 ? Math.round((data.total_with_vertical / total) * 100) : 0

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <p className="text-steel text-footnote font-semibold uppercase tracking-widest mb-2" style={{ fontFamily: '"JetBrains Mono", monospace' }}>Vertical Coverage</p>
        <p className="text-chalk text-h3 font-bold">
          {data.total_with_vertical} <span className="text-steel text-body">of {total} users ({pct}%) have provided vertical data</span>
        </p>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 tablet:grid-cols-2 gap-6">
        <VerticalBarChart title="Users by Vertical" data={data.users_by_vertical} dataKey="vertical" />
        <VerticalBarChart title="Waitlist by Vertical" data={data.waitlist_by_vertical} dataKey="vertical" />
        <VerticalBarChart title="Demo Signals by Vertical" data={data.demo_by_vertical} dataKey="vertical" />
        <VerticalBarChart title="Replacing (What We Replace)" data={data.replacing_by_option} dataKey="replacing" />
      </div>
    </div>
  )
}

// ── Tab: PMF Score ────────────────────────────────────────────────────────────

function PmfScoreTab({ data }: { data: PmfAnalytics }) {
  const c = useThemeColors()
  const { survey_responses, activation, retention, demo_to_signup } = data

  const score = survey_responses.score
  const status = score >= 40 ? 'PMF Found' : score >= 20 ? 'Building' : 'Pre-PMF'
  const statusColor = score >= 40 ? 'text-emerald-400' : score >= 20 ? 'text-amber-400' : 'text-red-400'
  const statusBg = score >= 40 ? 'bg-emerald-400/10 border-emerald-400/30' : score >= 20 ? 'bg-amber-400/10 border-amber-400/30' : 'bg-red-400/10 border-red-400/30'

  const progressPct = Math.min((score / 40) * 100, 100)

  const funnelSteps = [
    { label: 'Signups', count: activation.signups_total, pct: 100 },
    {
      label: 'Ran first analysis',
      count: activation.ran_at_least_one,
      pct: activation.signups_total > 0 ? Math.round((activation.ran_at_least_one / activation.signups_total) * 100) : 0,
    },
    {
      label: 'Ran 2+ analyses',
      count: activation.ran_at_least_two,
      pct: activation.signups_total > 0 ? Math.round((activation.ran_at_least_two / activation.signups_total) * 100) : 0,
    },
  ]

  const funnelMax = Math.max(activation.signups_total, 1)

  return (
    <div className="space-y-6">

      {/* Section 1 — North Star: PMF Score */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-steel text-footnote uppercase tracking-wide mb-1">PMF Score (Superhuman Method)</p>
            <div className="flex items-end gap-4">
              <span className="text-chalk text-display-l">
                {survey_responses.total > 0 ? `${score}%` : '--'}
              </span>
              <div className="mb-2">
                <span className={`text-footnote font-semibold px-2 py-1 rounded border ${statusBg} ${statusColor}`}>
                  {status}
                </span>
              </div>
            </div>
            <p className="text-steel text-body-sm mt-2">Target: 40% "very disappointed"</p>
          </div>
        </div>

        {/* Progress bar toward 40% target */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-steel text-footnote">Progress to PMF threshold</span>
            <span className="text-steel text-footnote">{score}% / 40%</span>
          </div>
          <div className="relative h-3 bg-carbon rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%`, background: c.signal }}
            />
            {/* Target line at 100% of bar (represents 40%) */}
            <div
              className="absolute top-0 bottom-0 w-px"
              style={{ left: '100%', background: c.textMuted, borderLeft: `2px dashed ${c.textMuted}` }}
            />
          </div>
        </div>

        {/* Note about survey */}
        <div className="mt-4 p-3 bg-amber-400/5 border border-amber-400/20 rounded text-amber-300/70 text-footnote">
          {survey_responses.note}
        </div>
      </div>

      {/* Section 2 — Activation Funnel */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-5">Activation Funnel</h3>
        <div className="space-y-4">
          {funnelSteps.map(({ label, count, pct }, i) => (
            <div key={label}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-steel text-footnote font-mono">{i + 1}</span>
                  <span className="text-steel text-body-sm">{label}</span>
                </div>
                <span className="text-chalk text-body-sm font-medium">
                  {count.toLocaleString()}
                  {i > 0 && (
                    <span className="text-steel text-footnote ml-2">
                      ({pct}% of signups)
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 bg-carbon rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.round((count / funnelMax) * 100)}%`,
                    background: c.signal,
                    opacity: i === 0 ? 1 : i === 1 ? 0.75 : 0.5,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section 3 — Retention */}
      <div>
        <h3 className="text-chalk font-medium mb-3">Retention</h3>
        <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
          <MetricCard
            label="Repeat Users (7d)"
            value={retention.repeat_users_7d.toLocaleString()}
            sub={<span className="text-steel text-footnote">users with ≥2 analyses in last 7 days</span>}
          />
          <MetricCard
            label="Repeat Users (30d)"
            value={retention.repeat_users_30d.toLocaleString()}
            sub={<span className="text-steel text-footnote">users with ≥2 analyses in last 30 days</span>}
          />
        </div>
      </div>

      {/* Section 4 — PMF Survey Status */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-4">PMF Survey Status</h3>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
            <span className="text-chalk text-body-sm font-medium">Survey not yet active</span>
          </div>
          <div className="pl-4 space-y-2 text-steel text-body-sm">
            <p>Will launch when product is live (target: May 2026)</p>
            <p>Will send to users who have run ≥2 analyses in the past 2 weeks</p>
          </div>
          <div className="mt-4 p-4 bg-carbon/30 rounded border border-carbon">
            <p className="text-steel text-footnote uppercase tracking-wide mb-2">Key Question</p>
            <p className="text-chalk text-body-sm italic">
              "How would you feel if you could no longer use Primate Vision?"
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {['Very disappointed', 'Somewhat disappointed', 'Not disappointed'].map((opt, i) => (
                <span
                  key={opt}
                  className="text-footnote px-2 py-1 rounded border border-carbon text-steel"
                  style={{ opacity: 1 - i * 0.2 }}
                >
                  {opt}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Section 5 — Demo Signal Coverage */}
      <div className="bg-obsidian border border-carbon rounded p-6">
        <h3 className="text-chalk font-medium mb-4">Demo Signal Coverage</h3>
        <div className="flex items-center gap-6">
          <div>
            <p className="text-steel text-footnote uppercase tracking-wide mb-1">Demo signals captured</p>
            <p className="text-chalk text-h3 font-bold">{demo_to_signup.demo_signals_total.toLocaleString()}</p>
          </div>
          <div className="text-steel text-h4">→</div>
          <div>
            <p className="text-steel text-footnote uppercase tracking-wide mb-1">Signups</p>
            <p className="text-chalk text-h3 font-bold">{demo_to_signup.signups_total.toLocaleString()}</p>
          </div>
          <div className="flex-1" />
          <div className="text-right">
            <p className="text-steel text-footnote uppercase tracking-wide mb-1">Coverage ratio</p>
            <p className="text-chalk text-title-1">
              {demo_to_signup.signups_total > 0
                ? `${Math.round((demo_to_signup.demo_signals_total / demo_to_signup.signups_total) * 10) / 10}×`
                : '—'}
            </p>
            <p className="text-steel text-footnote mt-1">demo signals per signup</p>
          </div>
        </div>
        <p className="text-steel text-footnote mt-4">
          High ratio = anonymous users exploring before signing up. Confirms tracking is working.
        </p>
      </div>

    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type TabId = 'northstar' | 'acquisition' | 'revenue' | 'verticals' | 'use-cases' | 'pmf'
type RangeId = '12w' | '6m' | 'all'

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'northstar', label: 'North Star' },
  { id: 'acquisition', label: 'Acquisition' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'verticals', label: 'Verticals' },
  { id: 'pmf', label: 'PMF Score' },
  { id: 'use-cases', label: 'Use Cases' },
]

const RANGES: Array<{ id: RangeId; label: string }> = [
  { id: '12w', label: '12W' },
  { id: '6m', label: '6M' },
  { id: 'all', label: 'All Time' },
]

// ── Tab: Use Cases ────────────────────────────────────────────────────────────

const PIE_COLORS = ['#2058F0', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2', '#BE185D', '#6366F1']

function UseCasesTab({ verticals, demos }: { verticals: VerticalAnalytics | null; demos: DemoVideoStat[] }) {
  const c = useThemeColors()
  const usersByVertical = (verticals?.users_by_vertical ?? []).filter(v => v.count > 0)
  const totalUsers = usersByVertical.reduce((sum, v) => sum + v.count, 0)

  const pieData = usersByVertical.map(v => ({
    name: v.label.length > 30 ? v.label.slice(0, 28) + '…' : v.label,
    value: v.count,
    pct: totalUsers > 0 ? Math.round((v.count / totalUsers) * 1000) / 10 : 0,
  }))

  const barData = demos.slice(0, 8).map(d => ({
    name: d.name.length > 20 ? d.name.slice(0, 18) + '…' : d.name,
    selections: d.count,
    pct: d.pct,
  }))

  return (
    <div className="space-y-8">
      {/* Pie chart: User verticals */}
      <div className="bg-obsidian border border-carbon rounded-lg p-6">
        <h2 className="text-chalk font-semibold mb-1">Use Case Distribution</h2>
        <p className="text-steel text-footnote mb-6">Verticals selected by signed-up users during onboarding</p>
        {pieData.length === 0 ? (
          <div className="text-center py-12 text-steel text-body-sm">No vertical data for this period</div>
        ) : (
          <div className="flex gap-8 items-center">
            <PieChart width={260} height={260}>
              <Pie
                data={pieData}
                cx={124}
                cy={124}
                innerRadius={70}
                outerRadius={120}
                paddingAngle={2}
                dataKey="value"
              >
                {pieData.map((_entry, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
            </PieChart>
            <div className="flex-1 space-y-2">
              {pieData.map((entry, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div style={{ background: PIE_COLORS[i % PIE_COLORS.length], width: 10, height: 10, borderRadius: 2, flexShrink: 0 }} />
                  <span className="text-chalk text-footnote flex-1 truncate">{entry.name}</span>
                  <span className="text-steel text-footnote font-mono">{entry.value}</span>
                  <span style={{ color: PIE_COLORS[i % PIE_COLORS.length] }} className="text-footnote font-mono w-12 text-right">{entry.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stacked bar chart: Demo video popularity */}
      <div className="bg-obsidian border border-carbon rounded-lg p-6">
        <h2 className="text-chalk font-semibold mb-1">Demo Video Popularity</h2>
        <p className="text-steel text-footnote mb-6">Inferences run per demo video (proxy for user selections)</p>
        {barData.length === 0 ? (
          <div className="text-center py-12 text-steel text-body-sm">No demo video data for this period</div>
        ) : (
          <div className="space-y-3">
            {barData.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="text-steel text-footnote font-mono w-4 text-right">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-chalk text-footnote truncate">{d.name}</span>
                    <span className="text-steel text-footnote font-mono ml-2">{d.selections} · {d.pct}%</span>
                  </div>
                  <div style={{ background: c.border, borderRadius: 3, overflow: 'hidden', height: 8 }}>
                    <div style={{ width: `${d.pct}%`, height: '100%', background: PIE_COLORS[i % PIE_COLORS.length], borderRadius: 3 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function AdminGrowth() {
  const [tab, setTab] = useState<TabId>('northstar')
  const [range, setRange] = useState<RangeId>('12w')
  const [data, setData] = useState<GrowthAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [verticalData, setVerticalData] = useState<VerticalAnalytics | null>(null)
  const [verticalLoading, setVerticalLoading] = useState(false)
  const [verticalError, setVerticalError] = useState<string | null>(null)
  const [useCasesData, setUseCasesData] = useState<{ verticals: VerticalAnalytics | null; demos: DemoVideoStat[] }>({ verticals: null, demos: [] })
  const [useCasesLoading, setUseCasesLoading] = useState(false)
  const [useCasesError, setUseCasesError] = useState<string | null>(null)
  const [pmfData, setPmfData] = useState<PmfAnalytics | null>(null)
  const [pmfLoading, setPmfLoading] = useState(false)
  const [pmfError, setPmfError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    getGrowthAnalytics(range)
      .then(setData)
      .catch((e: Error) => setError(e.message ?? 'Failed to load'))
      .finally(() => setLoading(false))
  }, [range])

  useEffect(() => {
    if (tab !== 'verticals') return
    if (verticalData) return // already loaded
    setVerticalLoading(true)
    setVerticalError(null)
    getVerticalAnalytics()
      .then(setVerticalData)
      .catch((e: Error) => setVerticalError(e.message ?? 'Failed to load verticals'))
      .finally(() => setVerticalLoading(false))
  }, [tab, verticalData])

  useEffect(() => {
    if (tab !== 'use-cases') return
    setUseCasesLoading(true)
    setUseCasesError(null)
    Promise.all([
      getVerticalAnalytics(range),
      getDemoVideoAnalytics(range),
    ])
      .then(([vert, demo]) => setUseCasesData({ verticals: vert, demos: demo.videos }))
      .catch((e: Error) => setUseCasesError(e.message ?? 'Failed to load'))
      .finally(() => setUseCasesLoading(false))
  }, [tab, range])

  useEffect(() => {
    if (tab !== 'pmf') return
    if (pmfData) return
    setPmfLoading(true)
    setPmfError(null)
    getPmfAnalytics()
      .then(setPmfData)
      .catch((e: Error) => setPmfError(e.message ?? 'Failed to load PMF data'))
      .finally(() => setPmfLoading(false))
  }, [tab, pmfData])

  return (
    <div className="min-h-screen bg-void p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-chalk text-h4">Growth</h1>
        <div className="flex items-center gap-1 bg-obsidian border border-carbon rounded p-1">
          {RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`px-3 py-1 rounded text-body-sm transition-colors ${
                range === r.id
                  ? 'bg-signal/20 text-signal'
                  : 'text-steel hover:text-chalk'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-carbon mb-6">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-body-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? 'border-signal text-chalk'
                : 'border-transparent text-steel hover:text-chalk'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-red-400 text-body-sm mb-6">
          {error}
        </div>
      )}

      {/* Loading skeletons */}
      {loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 tablet:grid-cols-3 gap-4">
            {[0, 1, 2].map(i => <SkeletonCard key={i} />)}
          </div>
          <SkeletonChart />
          <SkeletonChart />
        </div>
      )}

      {/* Tab content */}
      {tab !== 'verticals' && !loading && data && (
        <>
          {tab === 'northstar' && <NorthStarTab data={data} />}
          {tab === 'acquisition' && <AcquisitionTab data={data} />}
          {tab === 'revenue' && <RevenueTab data={data} />}
        </>
      )}

      {tab === 'use-cases' && (
        <>
          {useCasesError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-red-400 text-body-sm mb-6">{useCasesError}</div>
          )}
          {useCasesLoading && <div className="space-y-6"><SkeletonChart /><SkeletonChart /></div>}
          {!useCasesLoading && <UseCasesTab verticals={useCasesData.verticals} demos={useCasesData.demos} />}
        </>
      )}

      {tab === 'verticals' && (
        <>
          {verticalError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-red-400 text-body-sm mb-6">
              {verticalError}
            </div>
          )}
          {verticalLoading && (
            <div className="space-y-6">
              <SkeletonCard />
              <SkeletonChart />
              <SkeletonChart />
            </div>
          )}
          {!verticalLoading && verticalData && <VerticalsTab data={verticalData} />}
          {!verticalLoading && !verticalData && !verticalError && (
            <div className="text-center py-16 text-steel text-body-sm">
              No vertical data yet — data will appear as users complete onboarding
            </div>
          )}
        </>
      )}

      {tab === 'pmf' && (
        <>
          {pmfError && (
            <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-red-400 text-body-sm mb-6">
              {pmfError}
            </div>
          )}
          {pmfLoading && (
            <div className="space-y-6">
              <SkeletonCard />
              <SkeletonChart />
              <SkeletonChart />
            </div>
          )}
          {!pmfLoading && pmfData && <PmfScoreTab data={pmfData} />}
        </>
      )}
    </div>
  )
}
